import { HubConnectionState } from '@microsoft/signalr';
import React, {
  ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Modal as RSModal } from 'reactstrap';
import { BulkEditStatus } from 'src/components/common/BulkActions/BulkEditStatus';
import { SeatingInfo } from 'src/components/common/SeatingInfo';
import {
  IPosEntity,
  useActivePosEntityContext,
} from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import {
  BulkEditStage,
  useBulkEditHubContext,
} from 'src/contexts/BulkEditHubContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content } from 'src/contexts/ContentContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { WarningMessage } from 'src/core/POS/MessageWithIcon';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { PosTextField } from 'src/core/POS/PosTextField';
import { Button, SimpleTable, Stack } from 'src/core/ui';
import { Card } from 'src/core/ui';
import { useBulkEditHub } from 'src/hooks/useBulkEditHub';
import { useSearchV2DataRefetch } from 'src/hooks/useSearchV2DataRefetch';
import { Checkbox } from 'src/core/interim/Checkbox/Checkbox';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  BulkActionType,
  BulkEditListingClient,
  BulkEditPreviewWithDetails,
  BulkEditProgress,
  BulkEditStep,
  Event,
  Feature,
  Listing,
  ListingQuery,
  ValueTupleOfBulkEditPreviewAndIDictionaryOfLongAndListOfInteger,
} from 'src/WebApiController';

import { BulkEditFooter } from '../common/BulkEditFooter';
import { BulkEditHeader } from '../common/BulkEditHeader';
import * as styles from './BulkSplitSelectedListingsDialog.css';

export interface BulkSplitListingsForm {
  // Key is availableQty, value contains number of groups and split allocations
  splitConfigs: Record<
    number,
    {
      numberOfGroups: number;
      splitAllocations: number[]; // Array where index represents group number and value is ticket count
    }
  >;
  retainSeatTraits: boolean;
}

export interface GroupedListings {
  availableQuantity: number;
  listings: Listing[];
}

export type BulkSplitSelectedListingsDialogProps = ComponentProps<
  typeof RSModal
> & {
  updateKey: string;
  onOkay: (
    input?: { [key: string]: number[] },
    supportBackgroundProcess?: boolean,
    onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
    preview?: BulkEditPreviewWithDetails,
    retainSeatTraits?: boolean
  ) => void;
  onCancel: () => void;
  filterQueryWithEventIds: ListingQuery;
  event?: Event;
};

export const BulkSplitSelectedListingsDialog = ({
  updateKey,
  onOkay,
  onCancel,
  event,
  filterQueryWithEventIds,
  ...dialogProps
}: BulkSplitSelectedListingsDialogProps) => {
  const { showErrorDialog } = useClientLogContext();
  const requiredText = <Content id={ContentId.Required} />;
  const entityType = ActionOutboxEntityType.Listing;
  const { setProgress, setPreview, setMainDialogOpened } =
    useBulkEditHubContext();
  const { searchV2DataDelayedRefetch } = useSearchV2DataRefetch();
  const [isLoading, setIsLoading] = useState(false);
  const [isListingDataLoading, setIsListingDataLoading] = useState(false);
  const methods = useForm<BulkSplitListingsForm>({
    defaultValues: {
      splitConfigs: {},
      retainSeatTraits: false,
    },
  });
  const { control, handleSubmit, formState, watch, setValue } = methods;
  const hasSplitListingWithSeatTraitsRetainedFeature = useUserHasFeature(
    Feature.SplitListingWithSeatTraitsRetained
  );
  const retainSeatTraitsValue = watch('retainSeatTraits');

  // Default retainSeatTraits to true when the flag is on, false when off.
  useEffect(() => {
    setValue('retainSeatTraits', hasSplitListingWithSeatTraitsRetainedFeature);
  }, [hasSplitListingWithSeatTraitsRetainedFeature, setValue]);

  const { activeAccountWebClientConfig } = useAppContext();
  const { setActivePosEntity } = useActivePosEntityContext<IPosEntity>();
  const { setSelectionMode } = useMultiSelectionContext();
  const {
    eventsTransformed,
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();

  const onClose = useCallback(
    async (
      newProgress?: BulkEditProgress,
      closeMainDialogForPopover?: boolean
    ) => {
      setValue('splitConfigs', {}); // Reset form on close
      setValue('retainSeatTraits', hasSplitListingWithSeatTraitsRetainedFeature); // Reset to flag-driven default on close
      if (newProgress?.step === BulkEditStep.Done) {
        setIsRefreshing(true);
        setActivePosEntity(0);

        await searchV2DataDelayedRefetch();

        if (refreshExpandedListItems) {
          await refreshExpandedListItems();
        }
        setSelectionMode(undefined);
        if (closeMainDialogForPopover) {
          setMainDialogOpened(false);
        } else {
          setProgress(undefined);
          setPreview(undefined);
        }
        setIsRefreshing(false);
        // progress kept to allow the BulkEditStatusPopover to show the result
      }
      onCancel();
    },
    [
      onCancel,
      refreshExpandedListItems,
      searchV2DataDelayedRefetch,
      setActivePosEntity,
      setMainDialogOpened,
      setPreview,
      setProgress,
      setSelectionMode,
      setValue,
    ]
  );

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [listingData, setListingData] = useState<Listing[] | null>(null);

  const submitButtonRef = useRef<HTMLButtonElement>(null);

  const onBulkEditDone = useCallback(
    async (doneProgress: BulkEditProgress, finalErrors: string[]) => {
      if (finalErrors.length === 0 && doneProgress.step === BulkEditStep.Done) {
        setIsRefreshing(true);
        onClose(doneProgress, false);
      }
    },
    [onClose]
  );

  const { bulkEditHub, progress, preview, initJob, stage } = useBulkEditHub(
    entityType,
    BulkActionType.SplitListings,
    updateKey,
    onBulkEditDone
  );

  const supportBackgroundProcess = useMemo(
    () => bulkEditHub?.state === HubConnectionState.Connected,
    [bulkEditHub?.state]
  );

  // Group listings by available quantity only
  const groupedListings = useMemo<GroupedListings[]>(() => {
    if (!listingData) return [];

    const groups: Record<number, GroupedListings> = {};

    listingData.forEach((listing) => {
      const availQty = listing.availQty || 0;

      // listing with only 1 available ticket has no need to split
      if (availQty <= 1) {
        return;
      }

      if (!groups[availQty]) {
        groups[availQty] = {
          availableQuantity: availQty,
          listings: [],
        };
      }

      groups[availQty].listings.push(listing);
    });

    return Object.values(groups).toSorted(
      (a, b) => a.availableQuantity - b.availableQuantity
    );
  }, [listingData]);

  // event name lookup
  const eventLookup = useMemo<{ [key: number]: string | undefined }>(() => {
    if (!listingData || !eventsTransformed) return [];

    const lookup: { [key: string]: string | undefined } = {};

    listingData.forEach((listing) => {
      const event = eventsTransformed!.find(
        (ev) =>
          ev.event.virtId === listing.viagVirtId ||
          ev.event.posIds.some((id) => id == listing.posEvId)
      );

      lookup[listing.id] = event?.event?.name;
    });

    return lookup;
  }, [eventsTransformed, listingData]);

  const onSubmitHandler = useCallback(
    (onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void) => {
      handleSubmit((formValues) => {
        // Transform form data to the expected API format
        const splitConfigDict: { [key: number]: number[] } = {};

        // Map each available quantity group to listing IDs that need to be split
        Object.entries(formValues.splitConfigs).forEach(
          ([availQty, config]) => {
            if (
              config.numberOfGroups > 0 &&
              config.splitAllocations?.length > 0
            ) {
              // Find all listings with this available quantity
              const availQtyNum = parseInt(availQty);
              const matchingGroup = groupedListings.find(
                (group) => group.availableQuantity === availQtyNum
              );

              if (matchingGroup) {
                matchingGroup.listings.forEach((listing) => {
                  splitConfigDict[listing.id] = config.splitAllocations;
                });
              }
            }
          }
        );

        setIsLoading(true);
        if (onPreviewReceived) {
          onOkay(undefined, false, onPreviewReceived);
        } else {
          onOkay(splitConfigDict, supportBackgroundProcess, undefined, preview, formValues.retainSeatTraits);
        }
        setIsLoading(false);
      })();
    },
    [handleSubmit, onOkay, preview, supportBackgroundProcess, groupedListings]
  );

  const configs = watch('splitConfigs');
  const isFormValid = useCallback(() => {
    return (
      Object.keys(configs).length > 0 &&
      Object.keys(configs).length == groupedListings.length &&
      Object.entries(configs).every(([key, config]) => {
        if (
          config.splitAllocations == null ||
          config.splitAllocations.some((sa) => sa == 0 || sa == null) ||
          config.splitAllocations.length < config.numberOfGroups
        ) {
          return false;
        }
        const availQty = parseInt(key);
        const total =
          config.splitAllocations?.reduce((sum, val) => sum + val, 0) || 0;
        return config.numberOfGroups > 1 && total === availQty;
      })
    );
  }, [configs, groupedListings]);

  return (
    <FormProvider {...methods}>
      <GenericDialog
        {...dialogProps}
        size={stage === BulkEditStage.Preview ? 'xl' : 'lg'}
        header={
          <BulkEditHeader
            headerText={<Content id={ContentId.SplitSelectedListings} />}
          />
        }
        footer={
          isRefreshing ? null : (
            <BulkEditFooter
              entityType={entityType}
              isLoading={isLoading}
              hasBackgroundBulkEditFeature={supportBackgroundProcess}
              onClose={() => {
                setValue('splitConfigs', {}); // Reset form on close
                setValue('retainSeatTraits', hasSplitListingWithSeatTraitsRetainedFeature); // Reset to flag-driven default on close
                setMainDialogOpened(false);
                if (progress) {
                  setSelectionMode(undefined);
                }
                // Call the outside one if there is one
                onCancel();
              }}
              onSubmit={onSubmitHandler}
              submittButtonRef={submitButtonRef}
              disabled={isLoading || !isFormValid()}
            />
          )
        }
        onClick={(e) => {
          // This so that this dialog does not cause event to flow to the EventHeader open/close event
          e.stopPropagation();
          e.preventDefault();
        }}
        onOpened={async () => {
          initJob();

          // load listing detail
          setIsListingDataLoading(true);

          await tryInvokeApi(
            async () => {
              const data = await new BulkEditListingClient(
                activeAccountWebClientConfig
              ).bulkSplitSelectedListingsPreview(filterQueryWithEventIds);

              setListingData(data?.listings);
            },
            (error) => {
              showErrorDialog('bulkSplitSelectedListingsPreview', error, {
                trackErrorData: { filterQueryWithEventIds },
              });
            },
            () => {
              setIsListingDataLoading(false);
            }
          );
        }}
        onClosed={() => {
          setMainDialogOpened(false);
          // Call the outside one if there is one
          onCancel();
        }}
        onCancel={isLoading ? undefined : onCancel}
      >
        <BulkEditStatus
          entityType={entityType}
          isLoading={isRefreshing || isLoading}
          updateKey={updateKey}
        >
          {isListingDataLoading ? (
            <PosSpinner />
          ) : (
            <Stack direction="column" gap="l">
              {/* Retain seat traits checkbox (visible when feature flag 742 is enabled) */}
              {hasSplitListingWithSeatTraitsRetainedFeature && (
                <Checkbox
                  checked={retainSeatTraitsValue}
                  onChange={(e) => setValue('retainSeatTraits', e.target.checked)}
                  label={<Content id={ContentId.SplitListing_RetainSeatTraits} />}
                  labelPosition="right"
                  data-testid="bulk-retain-seat-traits-checkbox"
                />
              )}
              {/* Render each available quantity group as separate sections */}
              {groupedListings.map((group, groupIndex) => (
                <div key={groupIndex}>
                  <Card className={styles.splitListingPreviewContainer}>
                    {/* Section Header with Total Listings Count */}
                    <Stack
                      direction="row"
                      width="full"
                      justifyContent="spaceBetween"
                      alignItems="center"
                    >
                      <span>
                        <Content id={ContentId.OriginalListings} />
                      </span>
                      <Stack direction="row" gap="m">
                        <span>
                          <Content id={ContentId.TotalListing} />
                        </span>
                        <span>{group.listings.length}</span>
                        <span>
                          <Content id={ContentId.OriginalQty} />
                        </span>
                        <span>{group.availableQuantity}</span>
                      </Stack>
                    </Stack>

                    {/* Original Listings Table */}
                    <div className={styles.tableWrapper}>
                      <SimpleTable.Table className={styles.table}>
                        <SimpleTable.Thead showBottomBorder>
                          <SimpleTable.Tr>
                            <SimpleTable.Th className={styles.tableHeader}>
                              <Content id={ContentId.ListingId} />
                            </SimpleTable.Th>
                            <SimpleTable.Th className={styles.tableHeader}>
                              <Content id={ContentId.Event} />
                            </SimpleTable.Th>
                            <SimpleTable.Th className={styles.tableHeader}>
                              <Content id={ContentId.Seating} />
                            </SimpleTable.Th>
                            <SimpleTable.Th className={styles.tableHeader}>
                              <Content id={ContentId.Quantity} />
                            </SimpleTable.Th>
                          </SimpleTable.Tr>
                        </SimpleTable.Thead>
                        <SimpleTable.Tbody>
                          {group.listings.map((listing) => (
                            <SimpleTable.Tr key={listing.id}>
                              <SimpleTable.Td>{listing.id}</SimpleTable.Td>
                              <SimpleTable.Td>
                                {eventLookup[listing.id] || 'N/A'}
                              </SimpleTable.Td>
                              <SimpleTable.Td>
                                <SeatingInfo
                                  section={listing.seating?.section}
                                  row={listing.seating?.row}
                                  seatFr={listing.seating?.seatFr}
                                  seatTo={listing.seating?.seatTo}
                                />
                              </SimpleTable.Td>
                              <SimpleTable.Td>
                                {listing.availQty}
                              </SimpleTable.Td>
                            </SimpleTable.Tr>
                          ))}
                        </SimpleTable.Tbody>
                      </SimpleTable.Table>
                    </div>

                    {/* Number of Groups Input */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      width="fit"
                      className={styles.numberOfGroupInputContainer}
                    >
                      <span style={{ width: 360 }}>
                        <Content id={ContentId.NumberOfGroups} />
                      </span>
                      <PosTextField
                        type="number"
                        min="1"
                        max={group.availableQuantity}
                        value={
                          configs?.[group.availableQuantity]?.numberOfGroups ||
                          ''
                        }
                        onChange={(e) => {
                          let numberOfGroups = parseInt(e.target.value) || 0;
                          if (numberOfGroups > group.availableQuantity) {
                            numberOfGroups = group.availableQuantity;
                          } else if (numberOfGroups < 0) {
                            numberOfGroups = 0;
                          }
                          const currentConfig =
                            configs?.[group.availableQuantity] || {};
                          const currentAllocations =
                            currentConfig.splitAllocations ||
                            Array(numberOfGroups).fill(undefined);

                          setValue('splitConfigs', {
                            ...configs,
                            [group.availableQuantity]: {
                              ...currentConfig,
                              numberOfGroups,
                              splitAllocations: currentAllocations.slice(
                                0,
                                numberOfGroups
                              ),
                            },
                          });
                        }}
                      />
                    </Stack>

                    {/* Dynamic Split Groups Table */}
                    {configs?.[group.availableQuantity]?.numberOfGroups > 0 && (
                      <div className={styles.tableWrapper}>
                        <SimpleTable.Table className={styles.table}>
                          <SimpleTable.Thead showBottomBorder>
                            <SimpleTable.Tr>
                              {Array.from(
                                {
                                  length:
                                    configs[group.availableQuantity]
                                      ?.numberOfGroups || 0,
                                },
                                (_, i) => (
                                  <SimpleTable.Th
                                    key={i}
                                    className={styles.tableHeader}
                                  >
                                    <Content id={ContentId.Split} /> {i + 1}
                                  </SimpleTable.Th>
                                )
                              )}
                            </SimpleTable.Tr>
                          </SimpleTable.Thead>
                          <SimpleTable.Tbody>
                            <SimpleTable.Tr>
                              {Array.from(
                                {
                                  length:
                                    configs[group.availableQuantity]
                                      ?.numberOfGroups || 0,
                                },
                                (_, i) => (
                                  <SimpleTable.Td key={i}>
                                    <PosTextField
                                      type="number"
                                      min="1"
                                      max={group.availableQuantity}
                                      value={
                                        configs[group.availableQuantity]
                                          ?.splitAllocations?.[i] || ''
                                      }
                                      onChange={(e) => {
                                        const value =
                                          parseInt(e.target.value) || 0;
                                        const currentConfig =
                                          configs[group.availableQuantity] ||
                                          {};
                                        const newAllocations = [
                                          ...(currentConfig.splitAllocations ||
                                            []),
                                        ];
                                        newAllocations[i] = value;

                                        setValue('splitConfigs', {
                                          ...configs,
                                          [group.availableQuantity]: {
                                            ...currentConfig,
                                            splitAllocations: newAllocations,
                                          },
                                        });
                                      }}
                                    />
                                  </SimpleTable.Td>
                                )
                              )}
                            </SimpleTable.Tr>
                          </SimpleTable.Tbody>
                        </SimpleTable.Table>
                      </div>
                    )}
                  </Card>
                </div>
              ))}

              {groupedListings.length === 0 && (
                <WarningMessage
                  message={<Content id={ContentId.NoListingsToSplit} />}
                />
              )}
            </Stack>
          )}
        </BulkEditStatus>
      </GenericDialog>
    </FormProvider>
  );
};
